/**
 * O portão entre a tela da pessoa e a conversa.
 *
 * Até aqui o snapshot da tela saía do navegador em TODO turno, sem que ninguém
 * tivesse dito que podia. Isso é errado por dois motivos, e o segundo não é
 * sobre privacidade: a folha de ponto de um empregado é dado de terceiro, e o
 * modelo passava a raciocinar sobre trinta linhas que a pessoa não ofereceu.
 *
 * A regra agora é: o que descreve a tela sai; o que a tela MOSTRA não sai sem
 * autorização. Sem o "sim", o envelope carrega identidade e nada mais — é o
 * mínimo para o modelo poder PERGUNTAR ("quer que eu leia a folha de ponto que
 * está na sua frente?") em vez de oferecer no escuro. Com o "sim", o envelope
 * é o mesmo de antes desta change, byte a byte, mais a marca da autorização.
 *
 * `leitura_autorizada` é campo de wire, e não inferência do consumidor: um
 * envelope sem as três listas pode ser uma tela vazia OU uma tela negada, e as
 * duas pedem respostas opostas do modelo. Adivinhar qual é seria a mesma classe
 * de defeito que a lista única de campos foi.
 *
 * O envelope de envio é um TIPO próprio (`ContextoDeTelaEnviado`) e só nasce
 * aqui: quem tem um `ContextoDeTelaNaoAutoritativo` colhido do DOM não
 * consegue mandá-lo para a rede sem passar por este portão. É a invariante que
 * impede a próxima pessoa de reabrir o vazamento por descuido.
 */
import { ContextoDeTelaNaoAutoritativo } from './observadorDeTela'

/**
 * O envelope na forma em que ele pode ATRAVESSAR a rede. A marca é obrigatória
 * de propósito — ver a nota do módulo.
 */
export interface ContextoDeTelaEnviado extends ContextoDeTelaNaoAutoritativo {
  leitura_autorizada: boolean
}

/**
 * Segmento que é DADO, não nome de tela: id, data, período, uuid.
 *
 * A rota da folha de ponto é `/timesheet/employee/8231/2026-09`. Os dois
 * primeiros segmentos dizem QUE tela é; os dois últimos dizem DE QUEM e de
 * QUANDO — e isso é exatamente a informação que a pessoa ainda não autorizou.
 * Mandar a rota inteira no envelope negado entregaria pela porta dos fundos
 * metade do que as três listas entregam pela porta da frente.
 */
function ehSegmentoDeDado (segmento: string): boolean {
  if (segmento === '') return false
  if (/^\d+$/.test(segmento)) return true
  if (/^\d{4}-\d{2}(-\d{2})?$/.test(segmento)) return true
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segmento)) return true
  // Hex comprido: ObjectId do Mongo (24), sha (40/64), qualquer opaco.
  //
  // Esta linha nasceu de um vazamento medido: a regra anterior era "comprido e
  // sem vogal", e `6a87367ff8b079026d509708` — o id da empregada cuja folha
  // estava aberta — passou porque hexadecimal contém `a` e `e`. O id de quem é
  // exatamente o que o envelope negado não pode carregar.
  if (/^[0-9a-f]{16,}$/i.test(segmento)) return true
  // Opaco não-hex: comprido e sem vogal não é palavra de rota, é identificador.
  if (segmento.length >= 16 && !/[aeiou]/i.test(segmento)) return true
  return false
}

/**
 * A rota reduzida ao que ela tem de NOME. Query string sai inteira: filtro é
 * escolha da pessoa sobre os dados, e é dado.
 *
 * Nunca devolve vazio: o contrato do backend exige rota com ao menos um
 * caractere, e uma rota que só tinha identificadores continua sendo uma tela —
 * a barra sozinha é a identificação honesta que sobrou.
 */
export function rotaSemDados (rota: string): string {
  const semQuery = (rota || '').split(/[?#]/)[0]
  const preservados = semQuery
    .split('/')
    .filter(segmento => segmento !== '' && !ehSegmentoDeDado(segmento))
  if (preservados.length === 0) return '/'
  return `/${preservados.join('/')}`
}

/**
 * O envelope que vai no corpo da interação, ou `null` quando não há tela.
 *
 * Autorizado: tudo o que o observador colheu, mais a marca.
 * Negado: identidade e só. As três listas viajam VAZIAS e não ausentes — a
 * ausência já significa "esta forma de envelope não existe" no contrato do
 * backend, e aqui a lista existe e foi deliberadamente não preenchida. Os
 * contadores de omissão vão zerados pelo mesmo motivo: eles contam o que a
 * heurística de sensíveis reteve, e aqui nada chegou a ser colhido para reter.
 */
export function envelopeParaEnvio (
  contexto: ContextoDeTelaNaoAutoritativo | null,
  leituraAutorizada: boolean
): ContextoDeTelaEnviado | null {
  if (contexto === null) return null
  if (leituraAutorizada) return { ...contexto, leitura_autorizada: true }
  return {
    versao: contexto.versao,
    natureza: contexto.natureza,
    rota: rotaSemDados(contexto.rota),
    tela: contexto.tela,
    estrutura: [],
    dados: [],
    acoes_e_campos: [],
    campos_omitidos_por_sensibilidade: 0,
    campos_omitidos_por_limite: 0,
    leitura_autorizada: false
  }
}

//: Verbos com que se pede uma leitura, nas três línguas do produto.
const VERBOS_DE_LEITURA = '(?:l[eê]|leia|ler|lendo|olh[ae]|olhar|v[eê]|veja|ver|analis[ae]|analisar|consult[ae]|consultar|consider[ae]|considerar|interpret[ae]|interpretar|read|lee|mira|mirar|revisa|revisar)'

//: Como a pessoa chama o que está na frente dela.
const SUBSTANTIVOS_DE_TELA = '(?:tela|telinha|p[áa]gina|folha(?: de ponto)?|planilha|espelho|screen|page|pantalla|o que (?:eu )?estou vendo|o que t[áa] (?:a[íi]|na tela)|isso a[íi]|aqui)'

/**
 * A pessoa PEDIU, com todas as letras, que o Assistente leia o que está na
 * tela. É o outro caminho de autorização além do botão — e é o caminho que a
 * pessoa usa sem saber que existe um botão.
 *
 * Fail-CLOSED em toda dúvida. Uma negação em qualquer lugar da frase derruba o
 * casamento inteiro: "não precisa olhar a tela" e "olha a tela" são parecidos
 * para uma regex e opostos para quem escreveu. Autorizar por engano é o defeito
 * que este módulo existe para impedir; não autorizar por excesso de zelo custa
 * um clique no botão, que está sempre à mão.
 */
export function pediuLeituraDaTela (texto: string): boolean {
  const frase = (texto || '').trim()
  if (frase === '') return false
  // `sem` e o `no` de «no mês» ficam de fora da lista: são comuns demais em
  // português e derrubariam pedidos legítimos ("olha a folha de ponto no mês
  // passado"). O que resta nega de verdade.
  if (/\b(n[ãa]o|nunca|jamais|pare de|deixe de|don'?t|do not)\b/i.test(frase)) return false
  const padrao = new RegExp(`${VERBOS_DE_LEITURA}\\b[^.?!]{0,40}?\\b${SUBSTANTIVOS_DE_TELA}\\b`, 'i')
  return padrao.test(frase)
}
